import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosConfig';
import { ADIMLAR, AdimKey } from '../KatKarsiligAdimRehberi';

// v1.26.0 — Kat karşılığı ilerleme asistanı: eksik bilgiler, hesaplanabilen eksiklerin doldurulması,
// tüm taraflar için paylaşım planı ve (AI ayarı açıksa) öncelik sırası / talep yorumları.

interface AsistanBolum {
    bolumID: number; blokID: number | null; katID: number | null;
    cephe1ID: number | null; cephe2ID: number | null; cephe3ID: number | null; cephe4ID: number | null;
    bolumAdi: string; bolumTipi: string | null; turu: string | null; m2: number; fiyat: number; not: string | null;
}
interface Duzeltme { bolumID: number; bolumAdi: string; alan: 'fiyat' | 'm2'; deger: number; aciklama: string; }
interface PlanSatiri { tip: 'ortak' | 'muteahhit'; id: number; adSoyad: string; bolumID: number; bolumAdi: string; oran: number; tutar: number; }
interface AsistanSonucu {
    kaynak: 'ai' | 'kural'; aiAktif: boolean; ozet: string | null; planYorumu: string | null;
    oncelikler: { baslik: string; aciklama: string | null; adim: AdimKey | null }[];
    talepNotlari: { kisi: string; not: string }[];
    eksikler: { adim: AdimKey; metin: string }[];
    duzeltmeler: Duzeltme[];
    plan: PlanSatiri[];
}

const tl = (n: number) => `${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;
const adimAdi = (k: AdimKey | null) => ADIMLAR.find(a => a.key === k);

const KatKarsiligAiAsistan: React.FC<{
    arsaId: number;
    bolumler: AsistanBolum[];
    onGit: (k: AdimKey) => void;
    onTamam: () => Promise<void> | void;
    onKapat: () => void;
}> = ({ arsaId, bolumler, onGit, onTamam, onKapat }) => {
    const [veri, setVeri] = useState<AsistanSonucu | null>(null);
    const [yukleniyor, setYukleniyor] = useState(false);
    const [uygulanan, setUygulanan] = useState<Set<string>>(new Set());
    const [islemde, setIslemde] = useState<string | null>(null);

    const yukle = async () => {
        setYukleniyor(true);
        setUygulanan(new Set());
        try { setVeri((await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/ai-asistan`)).data); }
        catch { toast.error('Analiz alınamadı.'); }
        finally { setYukleniyor(false); }
    };
    useEffect(() => { yukle(); }, [arsaId]); // eslint-disable-line react-hooks/exhaustive-deps

    const duzeltmeUygula = async (d: Duzeltme, toplu = false) => {
        const b = bolumler.find(x => x.bolumID === d.bolumID);
        if (!b) return false;
        try {
            await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler/update/${b.bolumID}`, {
                blokID: b.blokID, katID: b.katID, cephe1ID: b.cephe1ID, cephe2ID: b.cephe2ID, cephe3ID: b.cephe3ID, cephe4ID: b.cephe4ID,
                bolumAdi: b.bolumAdi, bolumTipi: b.bolumTipi, turu: b.turu, not: b.not,
                m2: d.alan === 'm2' ? d.deger : b.m2, fiyat: d.alan === 'fiyat' ? d.deger : b.fiyat,
            });
            setUygulanan(s => new Set(s).add(`d-${d.bolumID}-${d.alan}`));
            if (!toplu) { toast.success(`${d.bolumAdi}: ${d.alan === 'fiyat' ? 'fiyat' : 'm²'} güncellendi.`); await onTamam(); }
            return true;
        } catch { toast.error(`${d.bolumAdi} güncellenemedi.`); return false; }
    };
    const planUygula = async (p: PlanSatiri, toplu = false) => {
        try {
            await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler/${p.bolumID}/paylasimlar`, {
                ortakID: p.tip === 'ortak' ? p.id : null, muteahhitID: p.tip === 'muteahhit' ? p.id : null,
                oranYuzde: Number(p.oran.toFixed(4)), aciklamaNot: 'AI asistan planı',
            });
            setUygulanan(s => new Set(s).add(`p-${p.tip}-${p.id}-${p.bolumID}`));
            if (!toplu) { toast.success(`${p.adSoyad} → ${p.bolumAdi} atandı.`); await onTamam(); }
            return true;
        } catch (e: any) {
            toast.error(`${p.bolumAdi}: ${typeof e?.response?.data === 'string' ? e.response.data : 'atanamadı'}`);
            return false;
        }
    };
    const tumunu = async (tur: 'd' | 'p') => {
        if (!veri) return;
        const liste = tur === 'd'
            ? veri.duzeltmeler.filter(d => !uygulanan.has(`d-${d.bolumID}-${d.alan}`))
            : veri.plan.filter(p => !uygulanan.has(`p-${p.tip}-${p.id}-${p.bolumID}`));
        if (liste.length === 0) return;
        if (!window.confirm(tur === 'd' ? `${liste.length} bölümün eksik değeri doldurulacak. Devam edilsin mi?` : `${liste.length} paylaşım oluşturulacak. Devam edilsin mi?`)) return;
        setIslemde(tur);
        let n = 0;
        for (const x of liste) if (tur === 'd' ? await duzeltmeUygula(x as Duzeltme, true) : await planUygula(x as PlanSatiri, true)) n++;
        if (n > 0) toast.success(`${n} işlem uygulandı.`);
        await onTamam();
        setIslemde(null);
    };

    return (
        <div className="kka-modal-overlay" onClick={onKapat}>
            <div className="kka-modal" style={{ maxWidth: 980, width: '96vw', maxHeight: '92vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                <div className="kka-modal-header">
                    <h3><i className="fas fa-magic" style={{ color: '#7c3aed', marginRight: 8 }}></i>AI Asistan — İlerleme Desteği</h3>
                    <button className="kka-modal-close" onClick={onKapat}><i className="fas fa-times"></i></button>
                </div>
                <div className="kka-modal-body">
                    {!veri ? (
                        <div className="kka-empty"><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Proje analiz ediliyor…</div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
                                <div className="kka-cozum" style={{ flex: 1, marginTop: 0 }}>
                                    <div style={{ marginBottom: 4 }}>
                                        <span className={`kka-kaynak${veri.kaynak === 'kural' ? ' kural' : ''}`}>
                                            <i className={`fas ${veri.kaynak === 'ai' ? 'fa-magic' : 'fa-calculator'}`}></i>
                                            {veri.kaynak === 'ai' ? 'AI analizi' : veri.aiAktif ? 'Kural tabanlı (AI yanıt vermedi)' : 'Kural tabanlı (yapay zekâ ayarı kapalı)'}
                                        </span>
                                    </div>
                                    {veri.ozet}
                                </div>
                                <button className="kka-btn-secondary" onClick={yukle} disabled={yukleniyor}>
                                    <i className={`fas fa-sync-alt${yukleniyor ? ' fa-spin' : ''}`} style={{ marginRight: 6 }}></i>Yeniden analiz et
                                </button>
                            </div>

                            {veri.oncelikler.length > 0 && (
                                <div className="kka-ai-bolum">
                                    <h4><i className="fas fa-list-ol" style={{ color: '#7c3aed' }}></i>Öncelik sırası</h4>
                                    {veri.oncelikler.map((o, i) => (
                                        <div key={i} className="kka-ai-oncelik">
                                            <span className="no">{i + 1}</span>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.86rem', color: '#1e1b4b' }}>{o.baslik}</div>
                                                {o.aciklama && <div style={{ fontSize: '0.8rem', color: '#4c1d95', marginTop: 2 }}>{o.aciklama}</div>}
                                            </div>
                                            {o.adim && adimAdi(o.adim) && (
                                                <button className="kka-mini-btn" onClick={() => { onGit(o.adim!); onKapat(); }}>
                                                    Adım {adimAdi(o.adim)!.no}: {adimAdi(o.adim)!.kisaAd} <i className="fas fa-arrow-right"></i>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {veri.kaynak === 'ai' && veri.eksikler.length > 0 && (
                                <div className="kka-ai-bolum">
                                    <h4><i className="fas fa-clipboard-list" style={{ color: '#d97706' }}></i>Tespit edilen eksikler</h4>
                                    {veri.eksikler.map((e, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', padding: '4px 0', borderBottom: '1px dashed #eef2f7' }}>
                                            <i className="fas fa-exclamation-circle" style={{ color: '#d97706' }}></i>
                                            <span style={{ flex: 1 }}>{e.metin}</span>
                                            <button className="kka-mini-btn" onClick={() => { onGit(e.adim); onKapat(); }}>Adım {adimAdi(e.adim)?.no}</button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {veri.duzeltmeler.length > 0 && (
                                <div className="kka-ai-bolum">
                                    <h4>
                                        <i className="fas fa-fill-drip" style={{ color: '#0369a1' }}></i>Hesaplanabilen eksikler ({veri.duzeltmeler.length})
                                        <button className="kka-mini-btn" style={{ marginLeft: 'auto' }} onClick={() => tumunu('d')} disabled={islemde !== null}>
                                            <i className={`fas ${islemde === 'd' ? 'fa-spinner fa-spin' : 'fa-check-double'}`}></i>Tümünü uygula
                                        </button>
                                    </h4>
                                    <table className="kka-sub-table" style={{ fontSize: '0.82rem' }}>
                                        <thead><tr><th>Bölüm</th><th>Alan</th><th>Önerilen</th><th>Hesap</th><th style={{ width: 80 }}></th></tr></thead>
                                        <tbody>
                                            {veri.duzeltmeler.map(d => {
                                                const ok = uygulanan.has(`d-${d.bolumID}-${d.alan}`);
                                                return (
                                                    <tr key={`${d.bolumID}-${d.alan}`} style={ok ? { opacity: 0.55 } : undefined}>
                                                        <td><strong>{d.bolumAdi}</strong></td>
                                                        <td>{d.alan === 'fiyat' ? 'Fiyat' : 'm²'}</td>
                                                        <td style={{ fontWeight: 700 }}>{d.alan === 'fiyat' ? tl(d.deger) : `${d.deger.toLocaleString('tr-TR')} m²`}</td>
                                                        <td style={{ color: '#64748b' }}>{d.aciklama}</td>
                                                        <td>{ok ? <span style={{ color: '#16a34a', fontWeight: 700 }}><i className="fas fa-check"></i> Uygulandı</span>
                                                            : <button className="kka-mini-btn" onClick={() => duzeltmeUygula(d)} disabled={islemde !== null}>Uygula</button>}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="kka-ai-bolum">
                                <h4>
                                    <i className="fas fa-hand-pointer" style={{ color: '#cd6906' }}></i>Önerilen paylaşım planı ({veri.plan.length})
                                    {veri.plan.length > 0 && (
                                        <button className="kka-mini-btn" style={{ marginLeft: 'auto' }} onClick={() => tumunu('p')} disabled={islemde !== null}>
                                            <i className={`fas ${islemde === 'p' ? 'fa-spinner fa-spin' : 'fa-check-double'}`}></i>Tümünü uygula
                                        </button>
                                    )}
                                </h4>
                                {veri.planYorumu && <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: 8 }}>{veri.planYorumu}</div>}
                                {veri.plan.length === 0 ? (
                                    <div className="kka-empty">Atanacak pay ya da hakkı eksik kalan taraf yok.</div>
                                ) : (
                                    <table className="kka-sub-table" style={{ fontSize: '0.82rem' }}>
                                        <thead><tr><th>Hak sahibi</th><th>Bölüm</th><th>Oran</th><th>Tutar</th><th style={{ width: 80 }}></th></tr></thead>
                                        <tbody>
                                            {veri.plan.map(p => {
                                                const ok = uygulanan.has(`p-${p.tip}-${p.id}-${p.bolumID}`);
                                                return (
                                                    <tr key={`${p.tip}-${p.id}-${p.bolumID}`} style={ok ? { opacity: 0.55 } : undefined}>
                                                        <td><strong>{p.adSoyad}</strong> <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{p.tip === 'ortak' ? 'Ortak' : 'Müteahhit'}</span></td>
                                                        <td>{p.bolumAdi}</td>
                                                        <td>%{p.oran.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}</td>
                                                        <td style={{ fontWeight: 700 }}>{tl(p.tutar)}</td>
                                                        <td>{ok ? <span style={{ color: '#16a34a', fontWeight: 700 }}><i className="fas fa-check"></i> Atandı</span>
                                                            : <button className="kka-mini-btn" onClick={() => planUygula(p)} disabled={islemde !== null}>Uygula</button>}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            {veri.talepNotlari.length > 0 && (
                                <div className="kka-ai-bolum">
                                    <h4><i className="fas fa-hand-paper" style={{ color: '#b45309' }}></i>Açık talepler için notlar</h4>
                                    {veri.talepNotlari.map((t, i) => (
                                        <div key={i} style={{ fontSize: '0.84rem', padding: '5px 0', borderBottom: '1px dashed #eef2f7' }}><strong>{t.kisi}:</strong> {t.not}</div>
                                    ))}
                                </div>
                            )}
                            <div style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                                Tutarlar ve oranlar sistem tarafından hesaplanır ve doğrulanır. {veri.aiAktif ? 'Yapay zekâya kişi adı, telefon ya da TC gönderilmez.' : ''}
                            </div>
                        </>
                    )}
                </div>
                <div className="kka-modal-footer">
                    <button className="kka-btn-secondary" onClick={onKapat}>Kapat</button>
                </div>
            </div>
        </div>
    );
};

export default KatKarsiligAiAsistan;
